import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "./UserPanel/Sidebar";

const EMPTY_FORM = {
  id: null,
  person: "",
  access_point: "",
  schedule: "",
  is_allowed: true,
  valid_from: "",
  valid_until: "",
};

function getApiMessage(data, fallback) {
  if (typeof data?.message === "string") {
    return data.message;
  }

  if (typeof data?.detail === "string") {
    return data.detail;
  }

  if (data?.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat(Infinity).join(" ");
  }

  return fallback;
}

function formatDate(value) {
  if (!value) {
    return "Sin límite";
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return "Sin límite";
  }

  return new Intl.DateTimeFormat("es-EC", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function getPermissionSource(source) {
  const labels = {
    PERSON: "Permiso individual",
    GROUP: "Permiso por rol",
    GROUP_EXCLUSION: "Exclusión del rol",
    NONE: "Sin permiso",
    PERSON_INACTIVE: "Usuario inactivo",
    USER_INACTIVE: "Cuenta inactiva",
  };

  return labels[source] || source || "Sin información";
}

function getPermissionReason(reason) {
  const labels = {
    ALLOWED: "Acceso permitido actualmente",
    PERMISSION_DENIED: "Acceso denegado",
    OUTSIDE_VALIDITY: "Fuera del período de vigencia",
    OUTSIDE_SCHEDULE: "Fuera del horario permitido",
    GROUP_EXCLUSION: "Excluido del permiso del rol",
    NO_PERMISSION: "No existe una regla asignada",
    PERSON_INACTIVE: "El usuario se encuentra inactivo",
    USER_INACTIVE: "La cuenta de acceso está inactiva",
  };

  return labels[reason] || reason || "Sin información";
}

export default function PersonPermissions() {
  const { fetchUser } = useAuth();

  const [organization, setOrganization] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [permissions, setPermissions] = useState([]);
  const [persons, setPersons] = useState([]);
  const [accessPoints, setAccessPoints] = useState([]);
  const [schedules, setSchedules] = useState([]);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [effectiveLoading, setEffectiveLoading] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [detailPermission, setDetailPermission] = useState(null);
  const [deletePermission, setDeletePermission] = useState(null);
  const [effectiveData, setEffectiveData] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [feedback, setFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showFeedback = useCallback((severity, message) => {
    setFeedback({
      open: true,
      severity,
      message,
    });
  }, []);

  const readJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [
        organizationResponse,
        permissionsResponse,
        personsResponse,
        accessPointsResponse,
        schedulesResponse,
      ] = await Promise.all([
        fetchUser("/tenants/api/logo/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/web/person-permissions/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/persons/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/web/access-points/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/web/schedules/", {
          method: "GET",
          cache: "no-store",
        }),
      ]);

      const organizationData = await readJson(organizationResponse);
      const permissionsData = await readJson(permissionsResponse);
      const personsData = await readJson(personsResponse);
      const accessPointsData = await readJson(accessPointsResponse);
      const schedulesData = await readJson(schedulesResponse);

      if (
        !organizationResponse.ok ||
        organizationData.ok !== true ||
        !organizationData.tenant
      ) {
        throw new Error(
          getApiMessage(
            organizationData,
            "No se pudo cargar la organización."
          )
        );
      }

      if (!permissionsResponse.ok || permissionsData.ok !== true) {
        throw new Error(
          getApiMessage(
            permissionsData,
            "No se pudieron cargar los permisos individuales."
          )
        );
      }

      if (!personsResponse.ok || personsData.ok !== true) {
        throw new Error(
          getApiMessage(
            personsData,
            "No se pudieron cargar los usuarios."
          )
        );
      }

      if (!accessPointsResponse.ok || accessPointsData.ok !== true) {
        throw new Error(
          getApiMessage(
            accessPointsData,
            "No se pudieron cargar los accesos."
          )
        );
      }

      if (!schedulesResponse.ok || schedulesData.ok !== true) {
        throw new Error(
          getApiMessage(
            schedulesData,
            "No se pudieron cargar los horarios."
          )
        );
      }

      setOrganization(organizationData.tenant);

      setPermissions(
        Array.isArray(permissionsData.permissions)
          ? permissionsData.permissions
          : []
      );

      setPersons(
        Array.isArray(personsData.persons)
          ? personsData.persons
          : []
      );

      setAccessPoints(
        Array.isArray(accessPointsData.access_points)
          ? accessPointsData.access_points
          : []
      );

      setSchedules(
        Array.isArray(schedulesData.schedules)
          ? schedulesData.schedules
          : []
      );
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo conectar con el servidor."
      );
    } finally {
      setLoading(false);
    }
  }, [fetchUser, showFeedback]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredPermissions = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return permissions;
    }

    return permissions.filter((permission) => {
      return (
        String(permission.person_name || "")
          .toLowerCase()
          .includes(query) ||
        String(permission.person_identification || "")
          .toLowerCase()
          .includes(query) ||
        String(permission.group_name || "")
          .toLowerCase()
          .includes(query) ||
        String(permission.access_point_name || "")
          .toLowerCase()
          .includes(query) ||
        String(permission.schedule_name || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [permissions, search]);

  const paginatedPermissions = useMemo(() => {
    const start = page * rowsPerPage;

    return filteredPermissions.slice(
      start,
      start + rowsPerPage
    );
  }, [filteredPermissions, page, rowsPerPage]);

  useEffect(() => {
    const lastPage = Math.max(
      0,
      Math.ceil(filteredPermissions.length / rowsPerPage) - 1
    );

    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [filteredPermissions.length, page, rowsPerPage]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (permission) => {
    setForm({
      id: permission.id,
      person: permission.person ?? "",
      access_point: permission.access_point ?? "",
      schedule: permission.schedule ?? "",
      is_allowed: permission.is_allowed === true,
      valid_from: permission.valid_from || "",
      valid_until: permission.valid_until || "",
    });

    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) {
      return;
    }

    setFormOpen(false);
    setForm(EMPTY_FORM);
  };

  const validateForm = () => {
    if (!form.person) {
      showFeedback("error", "Debes seleccionar un usuario.");
      return false;
    }

    if (!form.access_point) {
      showFeedback("error", "Debes seleccionar un acceso.");
      return false;
    }

    if (
      form.valid_from &&
      form.valid_until &&
      form.valid_from > form.valid_until
    ) {
      showFeedback(
        "error",
        "La fecha final no puede ser anterior a la fecha inicial."
      );
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const editing = Boolean(form.id);

      const response = await fetchUser(
        editing
          ? `/access-control/api/web/person-permissions/${form.id}/`
          : "/access-control/api/web/person-permissions/",
        {
          method: editing ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            person: Number(form.person),
            access_point: Number(form.access_point),
            schedule: form.schedule
              ? Number(form.schedule)
              : null,
            is_allowed: form.is_allowed,
            valid_from: form.valid_from || null,
            valid_until: form.valid_until || null,
          }),
        }
      );

      const data = await readJson(response);

      if (!response.ok || data.ok !== true) {
        throw new Error(
          getApiMessage(
            data,
            editing
              ? "No se pudo actualizar el permiso individual."
              : "No se pudo crear el permiso individual."
          )
        );
      }

      setFormOpen(false);
      setForm(EMPTY_FORM);

      showFeedback(
        "success",
        data.message ||
          (editing
            ? "Permiso individual actualizado correctamente."
            : "Permiso individual creado correctamente.")
      );

      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo guardar el permiso individual."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePermission) {
      return;
    }

    setDeletingId(deletePermission.id);

    try {
      const response = await fetchUser(
        `/access-control/api/web/person-permissions/${deletePermission.id}/`,
        {
          method: "DELETE",
        }
      );

      const data = await readJson(response);

      if (!response.ok || data.ok === false) {
        throw new Error(
          getApiMessage(
            data,
            "No se pudo eliminar el permiso individual."
          )
        );
      }

      setDeletePermission(null);

      showFeedback(
        "success",
        data.message ||
          "Permiso individual eliminado correctamente."
      );

      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo eliminar el permiso individual."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const loadEffectivePermissions = async (personId) => {
    setEffectiveLoading(true);
    setEffectiveData(null);

    try {
      const response = await fetchUser(
        `/access-control/api/web/persons/${personId}/effective-permissions/`,
        {
          method: "GET",
          cache: "no-store",
        }
      );

      const data = await readJson(response);

      if (!response.ok || data.ok !== true) {
        throw new Error(
          getApiMessage(
            data,
            "No se pudieron calcular los permisos efectivos."
          )
        );
      }

      setEffectiveData(data);
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudieron calcular los permisos efectivos."
      );
    } finally {
      setEffectiveLoading(false);
    }
  };

  if (loading && !organization) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        organization={organization}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          padding: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 2,
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                gap: 1,
              }}
            >
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  display: {
                    xs: "inline-flex",
                    md: "none",
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    color: "#111111",
                    fontSize: {
                      xs: 26,
                      md: 32,
                    },
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  Permisos individuales
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 14,
                    fontWeight: 500,
                    marginTop: 0.5,
                  }}
                >
                  {permissions.length} permisos registrados
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={openCreate}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                minHeight: 44,
                borderBottom: "3px solid #b00020",
              }}
            >
              Nuevo permiso
            </Button>
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: "1px solid #e1e1e4",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                padding: {
                  xs: 2,
                  sm: 2.5,
                },
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder="Buscar por usuario, identificación, rol, acceso u horario"
              />
            </Box>

            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuario</TableCell>
                      <TableCell>Rol</TableCell>
                      <TableCell>Acceso</TableCell>
                      <TableCell>Horario</TableCell>
                      <TableCell>Regla</TableCell>
                      <TableCell>Vigencia</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedPermissions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Box
                            sx={{
                              minHeight: 220,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography sx={{ fontWeight: 800 }}>
                              No hay permisos individuales
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedPermissions.map((permission) => (
                        <TableRow key={permission.id} hover>
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 800,
                              }}
                            >
                              {permission.person_name}
                            </Typography>

                            <Typography
                              sx={{
                                color: "#6b7280",
                                fontSize: 12,
                              }}
                            >
                              {permission.person_identification}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {permission.group_name || "Sin rol"}
                          </TableCell>

                          <TableCell>
                            {permission.access_point_name}
                          </TableCell>

                          <TableCell>
                            {permission.schedule_name || "Sin horario"}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                permission.is_allowed
                                  ? "Permitir"
                                  : "Denegar"
                              }
                              sx={{
                                fontWeight: 800,
                                color: permission.is_allowed
                                  ? "#047857"
                                  : "#b00020",
                                backgroundColor: permission.is_allowed
                                  ? "#ecfdf5"
                                  : "#fff1f2",
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Typography sx={{ fontSize: 12 }}>
                              {formatDate(permission.valid_from)}
                            </Typography>

                            <Typography
                              sx={{
                                color: "#6b7280",
                                fontSize: 12,
                              }}
                            >
                              hasta {formatDate(permission.valid_until)}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  setDetailPermission(permission)
                                }
                              >
                                Ver
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openEdit(permission)}
                              >
                                Editar
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  loadEffectivePermissions(
                                    permission.person
                                  )
                                }
                              >
                                Accesos efectivos
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() =>
                                  setDeletePermission(permission)
                                }
                              >
                                Eliminar
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box
              sx={{
                display: {
                  xs: "block",
                  md: "none",
                },
                padding: 2,
              }}
            >
              {paginatedPermissions.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    No hay permisos individuales
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {paginatedPermissions.map((permission) => (
                    <Box
                      key={permission.id}
                      sx={{
                        padding: 2,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          marginBottom: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 800,
                            }}
                          >
                            {permission.person_name}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#6b7280",
                              fontSize: 13,
                            }}
                          >
                            {permission.person_identification}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={
                            permission.is_allowed
                              ? "Permitir"
                              : "Denegar"
                          }
                          sx={{
                            flexShrink: 0,
                            fontWeight: 800,
                            color: permission.is_allowed
                              ? "#047857"
                              : "#b00020",
                            backgroundColor: permission.is_allowed
                              ? "#ecfdf5"
                              : "#fff1f2",
                          }}
                        />
                      </Box>

                      <Typography sx={{ fontSize: 13 }}>
                        <strong>Rol:</strong>{" "}
                        {permission.group_name || "Sin rol"}
                      </Typography>

                      <Typography sx={{ fontSize: 13, marginTop: 0.5 }}>
                        <strong>Acceso:</strong>{" "}
                        {permission.access_point_name}
                      </Typography>

                      <Typography sx={{ fontSize: 13, marginTop: 0.5 }}>
                        <strong>Horario:</strong>{" "}
                        {permission.schedule_name || "Sin horario"}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 13,
                          marginTop: 0.5,
                          marginBottom: 1.5,
                        }}
                      >
                        <strong>Vigencia:</strong>{" "}
                        {formatDate(permission.valid_from)} hasta{" "}
                        {formatDate(permission.valid_until)}
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setDetailPermission(permission)
                          }
                        >
                          Ver
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEdit(permission)}
                        >
                          Editar
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            loadEffectivePermissions(permission.person)
                          }
                          sx={{ gridColumn: "1 / -1" }}
                        >
                          Accesos efectivos
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() =>
                            setDeletePermission(permission)
                          }
                          sx={{ gridColumn: "1 / -1" }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <TablePagination
              component="div"
              count={filteredPermissions.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Filas"
              sx={{
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                },
              }}
            />
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {form.id
            ? "Editar permiso individual"
            : "Nuevo permiso individual"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ paddingTop: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Usuario</InputLabel>

              <Select
                value={form.person}
                label="Usuario"
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    person: event.target.value,
                  }))
                }
              >
                {persons
                  .filter(
                    (person) =>
                      person.is_active ||
                      person.id === Number(form.person)
                  )
                  .map((person) => (
                    <MenuItem key={person.id} value={person.id}>
                      {person.name} - {person.identification}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Acceso</InputLabel>

              <Select
                value={form.access_point}
                label="Acceso"
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    access_point: event.target.value,
                  }))
                }
              >
                {accessPoints
                  .filter(
                    (accessPoint) =>
                      accessPoint.is_active ||
                      accessPoint.id === Number(form.access_point)
                  )
                  .map((accessPoint) => (
                    <MenuItem
                      key={accessPoint.id}
                      value={accessPoint.id}
                    >
                      {accessPoint.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Horario</InputLabel>

              <Select
                value={form.schedule}
                label="Horario"
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    schedule: event.target.value,
                  }))
                }
              >
                <MenuItem value="">Sin horario</MenuItem>

                {schedules
                  .filter(
                    (schedule) =>
                      schedule.is_active ||
                      schedule.id === Number(form.schedule)
                  )
                  .map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                paddingX: 2,
                paddingY: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  marginBottom: 0.5,
                }}
              >
                Tipo de regla
              </Typography>

              <RadioGroup
                row
                value={form.is_allowed ? "allow" : "deny"}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    is_allowed: event.target.value === "allow",
                  }))
                }
              >
                <FormControlLabel
                  value="allow"
                  control={<Radio />}
                  label="Permitir acceso"
                />

                <FormControlLabel
                  value="deny"
                  control={<Radio />}
                  label="Denegar acceso"
                />
              </RadioGroup>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                type="date"
                label="Válido desde"
                value={form.valid_from}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    valid_from: event.target.value,
                  }))
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                fullWidth
                type="date"
                label="Válido hasta"
                value={form.valid_until}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    valid_until: event.target.value,
                  }))
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>

            <Alert severity="info">
              Este permiso tendrá prioridad sobre cualquier regla heredada
              del rol del usuario.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeForm} disabled={saving}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : form.id ? (
              "Guardar cambios"
            ) : (
              "Crear permiso"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detailPermission)}
        onClose={() => setDetailPermission(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Información del permiso individual</DialogTitle>

        <DialogContent>
          {detailPermission && (
            <Stack spacing={1.5} sx={{ paddingTop: 1 }}>
              {[
                ["Usuario", detailPermission.person_name],
                [
                  "Identificación",
                  detailPermission.person_identification,
                ],
                ["Rol", detailPermission.group_name || "Sin rol"],
                ["Acceso", detailPermission.access_point_name],
                [
                  "Horario",
                  detailPermission.schedule_name || "Sin horario",
                ],
                [
                  "Regla",
                  detailPermission.is_allowed
                    ? "Permitir acceso"
                    : "Denegar acceso",
                ],
                [
                  "Válido desde",
                  formatDate(detailPermission.valid_from),
                ],
                [
                  "Válido hasta",
                  formatDate(detailPermission.valid_until),
                ],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    paddingBottom: 1.2,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {label}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      marginTop: 0.3,
                      wordBreak: "break-word",
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailPermission(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={effectiveLoading || Boolean(effectiveData)}
        onClose={() => {
          if (!effectiveLoading) {
            setEffectiveData(null);
          }
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Accesos efectivos del usuario</DialogTitle>

        <DialogContent>
          {effectiveLoading ? (
            <Box
              sx={{
                minHeight: 260,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : effectiveData ? (
            <Stack spacing={2} sx={{ paddingTop: 1 }}>
              <Box
                sx={{
                  padding: 2,
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  backgroundColor: "#f9fafb",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 800,
                  }}
                >
                  {effectiveData.person?.name}
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 13,
                    marginTop: 0.3,
                  }}
                >
                  {effectiveData.person?.identification}
                  {effectiveData.person?.group?.name
                    ? ` · ${effectiveData.person.group.name}`
                    : " · Sin rol"}
                </Typography>
              </Box>

              {!effectiveData.permissions?.length ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    No existen accesos configurados
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {effectiveData.permissions.map((permission) => (
                    <Box
                      key={permission.access_point_id}
                      sx={{
                        padding: 2,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 800,
                            }}
                          >
                            {permission.access_point_name}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#6b7280",
                              fontSize: 12,
                              marginTop: 0.3,
                            }}
                          >
                            {getPermissionSource(permission.source)}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={
                            permission.is_currently_allowed
                              ? "Permitido ahora"
                              : "No permitido ahora"
                          }
                          sx={{
                            flexShrink: 0,
                            fontWeight: 800,
                            color: permission.is_currently_allowed
                              ? "#047857"
                              : "#b00020",
                            backgroundColor:
                              permission.is_currently_allowed
                                ? "#ecfdf5"
                                : "#fff1f2",
                          }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontSize: 13,
                          marginTop: 1.5,
                        }}
                      >
                        <strong>Resultado:</strong>{" "}
                        {getPermissionReason(permission.reason)}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 13,
                          marginTop: 0.5,
                        }}
                      >
                        <strong>Horario:</strong>{" "}
                        {permission.schedule_name || "Sin horario"}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 13,
                          marginTop: 0.5,
                        }}
                      >
                        <strong>Vigencia:</strong>{" "}
                        {formatDate(permission.valid_from)} hasta{" "}
                        {formatDate(permission.valid_until)}
                      </Typography>

                      {Array.isArray(permission.ranges) &&
                        permission.ranges.length > 0 && (
                          <Stack spacing={0.8} sx={{ marginTop: 1.5 }}>
                            {permission.ranges.map((range) => (
                              <Box
                                key={range.id}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 2,
                                  padding: 1,
                                  borderRadius: 1.5,
                                  backgroundColor: "#f9fafb",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 800,
                                  }}
                                >
                                  {range.weekday_name}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                  }}
                                >
                                  {String(
                                    range.start_time || ""
                                  ).slice(0, 5)}{" "}
                                  -{" "}
                                  {String(
                                    range.end_time || ""
                                  ).slice(0, 5)}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setEffectiveData(null)}
            disabled={effectiveLoading}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletePermission)}
        onClose={() => {
          if (!deletingId) {
            setDeletePermission(null);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar permiso individual</DialogTitle>

        <DialogContent>
          <Typography>
            ¿Deseas eliminar el permiso de{" "}
            {deletePermission?.person_name} para{" "}
            {deletePermission?.access_point_name}?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeletePermission(null)}
            disabled={Boolean(deletingId)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={Boolean(deletingId)}
          >
            {deletingId ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : (
              "Eliminar"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() =>
          setFeedback((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() =>
            setFeedback((previous) => ({
              ...previous,
              open: false,
            }))
          }
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}